import { HermesCollapse } from "@/backoffice/hermes-ui";
import { cn } from "@/utils/cn";
import { ChevronDown } from "lucide-react";
import type { NavChild, NavIcon } from "./backofficeNavItems";
import { ICON_HOVER, ICON_POP } from "./backofficeIconMotion";
import { BackofficeNavLeaf } from "./BackofficeNavLeaf";
import { BackofficeNavText } from "./BackofficeNavText";
import { NavUnreadDot } from "./NavUnreadDot";

type BackofficeNavGroupProps = {
  label: string;
  icon: NavIcon;
  items: NavChild[];
  /** Algum filho é a rota atual. */
  isActive: boolean;
  isOpen: boolean;
  isCollapsed: boolean;
  /** Sem acesso (ex.: a empresa não contratou): cinza claro, sem chevron e não abre. */
  isLocked?: boolean;
  /** Dica no hover explicando o bloqueio. */
  lockedHint?: string;
  /** Caminho atual, para marcar o filho selecionado. */
  pathname: string;
  onToggle: () => void;
  onNavigate: () => void;
};

/**
 * O filho mais específico que casa com a rota: em `/fidexa-pay/auditoria` só
 * "Auditoria" acende, e não também "Visão geral" (`/fidexa-pay`).
 */
const longestMatchingPath = (items: NavChild[], pathname: string) =>
  items
    .filter(item => pathname.startsWith(item.path))
    .reduce<
      string | undefined
    >((atual, item) => (atual === undefined || item.path.length > atual.length ? item.path : atual), undefined);

/**
 * Um grupo do menu: o botão abre e fecha a lista **animando a altura**, e o
 * chevron gira. Com o menu recolhido os filhos viram uma coluna de ícones; o
 * recuo e a linha-guia somem em transição, junto com os textos. Travado, o
 * botão fica cinza claro, perde o chevron e não abre.
 */
export const BackofficeNavGroup = ({
  label,
  icon: Icon,
  items,
  isActive,
  isOpen,
  isCollapsed,
  isLocked = false,
  lockedHint,
  pathname,
  onToggle,
  onNavigate,
}: BackofficeNavGroupProps) => {
  const activeChildPath = longestMatchingPath(items, pathname);
  // A closed group hides its children, so a child's notice dot moves up to
  // the group icon. Open, the child shows its own dot.
  const unreadTitle = isOpen
    ? undefined
    : items.find(item => item.unreadTitle)?.unreadTitle;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        disabled={isLocked}
        aria-expanded={isLocked ? undefined : isOpen}
        aria-label={isCollapsed ? label : undefined}
        aria-description={isLocked ? lockedHint : undefined}
        title={isLocked ? lockedHint : isCollapsed ? label : undefined}
        className={cn(
          "group hermes-focus-ring flex h-10 w-full cursor-pointer items-center rounded-xl px-3.5 text-sm font-medium",
          "transition-colors duration-150 motion-reduce:transition-none",
          "disabled:text-hermes-muted-fg/40 disabled:cursor-not-allowed",
          isActive
            ? "text-hermes-fg font-semibold"
            : "text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg disabled:hover:text-hermes-muted-fg/40 disabled:hover:bg-transparent"
        )}
      >
        <span className="relative shrink-0">
          <Icon
            // key: o pop recomeça cada vez que o grupo abre.
            key={isOpen ? "open" : "closed"}
            size={18}
            className={cn(
              "transition-[fill] duration-300",
              !isLocked && ICON_HOVER,
              isActive && "text-hermes-accent fill-current/20",
              isOpen && ICON_POP
            )}
          />
          {unreadTitle && !isLocked ? (
            <NavUnreadDot title={unreadTitle} />
          ) : null}
        </span>
        <BackofficeNavText visible={!isCollapsed}>{label}</BackofficeNavText>
        {!isLocked && (
          <BackofficeNavText trailing visible={!isCollapsed}>
            <ChevronDown
              size={16}
              aria-hidden="true"
              className={cn(
                "transition-transform duration-200 ease-out motion-reduce:transition-none",
                isOpen && "rotate-180"
              )}
            />
          </BackofficeNavText>
        )}
      </button>

      <HermesCollapse open={isOpen && !isLocked}>
        <div
          className={cn(
            "mt-0.5 flex flex-col gap-0.5 border-l",
            "transition-[margin,padding,border-color] duration-300 ease-out motion-reduce:transition-none",
            isCollapsed
              ? "ml-0 border-transparent pl-0"
              : "border-hermes-border ml-5 pl-2"
          )}
        >
          {items.map(item => (
            <BackofficeNavLeaf
              key={item.path}
              isChild
              label={item.label}
              path={item.path}
              icon={item.icon}
              isActive={item.isActive ?? item.path === activeChildPath}
              isCollapsed={isCollapsed}
              isLocked={item.comingSoon === true && !import.meta.env.DEV}
              opensInNewTab={item.opensInNewTab}
              unreadTitle={item.unreadTitle}
              onNavigate={() => {
                item.onSelect?.();
                onNavigate();
              }}
            />
          ))}
        </div>
      </HermesCollapse>
    </div>
  );
};
